import React, { useEffect, useMemo, useState } from 'react';
import CoreCreateModal, { SubModulePanel } from '../shared/CoreCreateModal';
import RecordWizard from '../reusable/formRecords/RecordWizards';
import { Field as FormField, Select as FormSelect, SectionHead, SectionDivider, ReadonlyBox, INP, INP_LOCKED } from '../reusable/formRecords/formFieldPrimitives';
import { useRegistryPlaceholder, useRegistryLabel } from '../Admin/FoundationalTables/FormRegistry/registryFieldOverrides';
import {
  actorQualificationsApi,
  cooperativesApi,
  farmerProducersApi,
  livestockActorsApi,
  workforceDeploymentsApi,
  AGE_GROUP_MAP,
} from '../../api/workforceApi';
import { useLocationOptions } from '../Admin/shared/useLocationOptions';
import { useWorkforceReferenceOptions } from './useWorkforceReferenceOptions';
import { useAuth } from '../../context/AuthContext';
import { MultiSelectPicklist, PhoneInput } from '../shared/forms/FormControls';

const ACTOR_TYPE_OPTS = [
  'Veterinarian',
  'Animal Scientist',
  'Extension Worker',
  'Livestock Inspector',
  'Abattoir Officer',
  'Vet Paraprofessional',
  'CAHW',
  'Cooperative Officer',
  'Trader',
  'Farmer Producer',
  'Processor',
  'Transporter',
  'Input Supplier',
  'Researcher',
  'Other',
];
const SEX_OPTS = ['Male', 'Female', 'Prefer Not To Say'];
const AGE_GROUP_OPTS = Object.entries(AGE_GROUP_MAP).map(([value, label]) => ({
  value,
  label,
}));
const STATUS_OPTS = ['Active', 'Inactive', 'Suspended'];
const VERIFY_OPTS = ['Verified', 'Pending', 'Rejected'];
const QUAL_TYPE_OPTS = [
  'Degree Vet Medicine',
  'Degree Animal Science',
  'Diploma',
  'Certificate',
  'Professional License',
  'Short Course',
  'CPD',
  'Other',
];
const DEPLOY_TYPE_OPTS = ['Permanent', 'Temporary', 'Field Mission', 'Training Attachment', 'Emergency'];
const MOBILITY_OPTS = ['Has Vehicle', 'Shared Vehicle', 'No Vehicle'];
const PRODUCTION_SYSTEMS = ['Pastoral', 'Agropastoral', 'Intensive', 'Semi Intensive', 'Backyard', 'Mixed'];
const HERD_CLASS_OPTS = ['Large', 'Medium', 'Small', 'Micro'];
const REG_AUTH_OPTS = ['CAC', 'FMLD', 'State Ministry', 'Cooperative Society', 'Other'];
const BANK_STATUS_OPTS = ['Active', 'Inactive', 'Pending'];
const COOP_TYPE_OPTS = [
  'Farmers Cooperative',
  'Commodity Association',
  'Processors Association',
  'Traders Association',
  'Womens Group',
  'Youth Group',
  'NGO',
  'Private Company',
  'Government Agency',
  'Other',
];

// A blank entry for the repeatable "Qualification" section embedded in the
// Actor Registration wizard — per the DCIC-08 field dictionary, [qual] on
// this form is just `type` and a conditional `expiry_date`.
function emptyQualificationEntry() {
  return {
    qualificationType: 'Degree Vet Medicine',
    expiryDate: '',
  };
}

const SUB_MODULES = [
  { value: 'actors', label: 'Livestock Actors', description: 'Register veterinarians, inspectors, traders, and other livestock actors.' },
  { value: 'deployment', label: 'Workforce Deployment Records', description: 'Track deployments, status, mobility, and supervising institutions.' },
  { value: 'coop', label: 'Cooperatives / Organisation', description: 'Manage cooperative registration, membership, and contact details.' },
];

const EMPTY_MAP = {
  actors: {
    fullName: '',
    contactPhone: '',
    actorType: 'Veterinarian',
    sex: 'Male',
    ageGroup: '26_to_35',
    state: '',
    lga: '',
    wardCommunity: '',
    status: 'Active',
    verificationStatus: 'Pending',
    speciesSpecialisation: [],
    productionSystem: '',
    species_uuid: '',
    herdSizeClass: '',
    farmSize: '',
    herdRegistrationNumber: '',
    population_record_uuid: '',
    primary_market_uuid: '',
    extension_officer_deployment_uuid: '',
    qualifications: [],
  },
  deployment: {
    livestock_actor_uuid: '',
    actorType: 'Veterinarian',
    institution_uuid: '',
    status: 'Active',
    deploymentType: 'Permanent',
    mobilityStatus: 'Has Vehicle',
    state: '',
    lga: '',
    wardCommunity: '',
    startDate: '',
    endDate: '',
    supervisor: '',
    responsibilities: '',
  },
  coop: {
    orgName: '',
    cooperativeType: 'Farmers Cooperative',
    regNumber: '',
    state: '',
    lga: '',
    wardCommunity: '',
    totalMembers: '',
    activeMembers: '',
    registeringAuthority: 'CAC',
    dateOfRegistration: '',
    bankAccountStatus: 'Pending',
    primaryCommodity: '',
    primaryActivities: '',
    contactPhone: '',
    contactEmail: '',
    status: 'Active',
  },
};

const VIEW_STATUS_DOT = {
  draft: 'bg-gray-400',
  submitted: 'bg-blue-500',
  validated: 'bg-amber-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
  active: 'bg-green-500',
  inactive: 'bg-gray-400',
  suspended: 'bg-amber-500',
  pending: 'bg-amber-500',
  verified: 'bg-green-500',
};

const VIEW_STATUS_PILL = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-50 text-blue-700',
  validated: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-600',
  active: 'bg-green-50 text-green-700',
  inactive: 'bg-gray-100 text-gray-600',
  suspended: 'bg-amber-50 text-amber-700',
  pending: 'bg-amber-50 text-amber-700',
  verified: 'bg-green-50 text-green-700',
};

// Row -> form mapping used when opening a record in 'view'/'edit' mode. The
// exact field lists here mirror what the old standalone Edit modals used to
// prefill (see git history of Livestockactor.jsx / WorkTab.jsx), so editing
// behaves identically even though it's now driven by the create-flow wizard.
const ROW_MAPPERS = {
  actors: (row) => ({
    ...EMPTY_MAP.actors,
    fullName: row.fullName ?? '',
    contactPhone: row.contactPhone ?? '',
    actorType: row.actorType ?? '',
    sex: row.sex ?? '',
    ageGroup: row.ageGroup ?? '',
    state: row.state ?? '',
    lga: row.lga ?? '',
    wardCommunity: row.wardCommunity ?? row.ward_community ?? '',
    status: row.status ?? '',
    verificationStatus: row.verificationStatus ?? '',
    speciesSpecialisation: String(row.speciesSpecialisation ?? row.species_specialisation ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  }),
  deployment: (row) => ({
    ...EMPTY_MAP.deployment,
    livestock_actor_uuid: row.livestock_actor_uuid ?? '',
    actorType: row.actorType ?? '',
    institution_uuid: row.institution_uuid ?? '',
    status: row.status ?? '',
    deploymentType: row.deploymentType ?? '',
    mobilityStatus: row.mobilityStatus ?? '',
    state: row.state ?? '',
    lga: row.lga ?? '',
    wardCommunity: row.wardCommunity ?? row.ward_community ?? '',
    startDate: row.startDate ?? '',
    endDate: row.endDate ?? '',
    supervisor: row.supervisor ?? '',
    responsibilities: row.responsibilities ?? '',
  }),
  coop: (row) => ({
    ...EMPTY_MAP.coop,
    orgName: row.orgName ?? '',
    cooperativeType: row.cooperativeType ?? EMPTY_MAP.coop.cooperativeType,
    regNumber: row.regNumber ?? '',
    state: row.state ?? '',
    lga: row.lga ?? '',
    wardCommunity: row.wardCommunity ?? row.ward_community ?? '',
    totalMembers: String(row.totalMembers ?? ''),
    activeMembers: String(row.activeMembers ?? ''),
    registeringAuthority: row.registeringAuthority ?? '',
    dateOfRegistration: row.dateOfRegistration ?? '',
    bankAccountStatus: row.bankAccountStatus ?? '',
    primaryCommodity: row.primaryCommodity ?? row.primary_commodity ?? '',
    primaryActivities: row.primaryActivities ?? '',
    contactPhone: row.contactPhone ?? '',
    contactEmail: row.contactEmail ?? '',
    status: row.status ?? '',
  }),
};

function Field({ label, required, hint, children }) {
  return (
    <FormField label={label} required={required} hint={hint}>
      {children}
    </FormField>
  );
}

function SelectField({
  label,
  required,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
}) {
  return (
    <FormField label={label} required={required}>
      <FormSelect
        value={value ?? ''}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        className={INP}
      />
    </FormField>
  );
}

function TextInput({ value, onChange, placeholder: pagePlaceholder = '', type = 'text', disabled = false }) {
  const placeholder = useRegistryPlaceholder(pagePlaceholder);
  return (
    <input
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      className={disabled ? INP_LOCKED : INP}
    />
  );
}

function TextArea({ value, onChange, placeholder: pagePlaceholder = '', rows = 3 }) {
  const placeholder = useRegistryPlaceholder(pagePlaceholder);
  return (
    <textarea
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className={`${INP} resize-none`}
    />
  );
}

function Notice({ error, loading }) {
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
        Loading reference options...
      </div>
    );
  }

  return null;
}

function SectionHeader({ title, description }) {
  return (
    <div className="space-y-1">
      <SectionHead title={title} />
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );
}

function ReviewRow({ label: pageLabel, value }) {
  const label = useRegistryLabel(pageLabel);
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      <span className="text-sm text-right text-gray-900">{value || value === 0 ? String(value) : '—'}</span>
    </div>
  );
}

// FK dropdowns store the option's uuid as the field value — resolve it back
// to the option's human-readable label for display in a review step,
// instead of showing the raw uuid the user never actually typed or saw.
function resolveLabel(options, value) {
  if (!value) return value;
  const match = (options ?? []).find((option) => String(option.value) === String(value));
  return match?.label ?? value;
}

function resolveLabels(options, values) {
  return (values ?? []).map((value) => resolveLabel(options, value)).join(', ');
}

// Repeatable "Qualification" section embedded in the Actor Registration
// wizard (DCIC-08) — lets an actor's qualifications be captured inline while
// registering them. Per the DCIC-08 field dictionary, the [qual] repeatable
// section on this form only carries `type` and (conditionally) `expiry_date`
// — the fuller qualification record (name, issuing institution/authority,
// issue date, credential number, evidence) belongs to the separate Actors
// Qualification tab, which remains available for adding/editing those on
// actors that already exist.
function QualificationsRepeatable({ entries, onChange }) {
  const updateEntry = (index, updates) => {
    onChange(entries.map((entry, i) => (i === index ? { ...entry, ...updates } : entry)));
  };
  const removeEntry = (index) => {
    onChange(entries.filter((_, i) => i !== index));
  };
  const addEntry = () => {
    onChange([...entries, emptyQualificationEntry()]);
  };

  return (
    <div className="space-y-3 rounded-2xl border border-green-100 bg-green-50/60 p-4">
      <SectionHeader
        title="Qualification (repeatable)"
        description="Optional — add one entry per professional qualification this actor holds."
      />

      {entries.map((entry, index) => {
        const isProfessionalLicense = String(entry.qualificationType ?? '').trim() === 'Professional License';
        return (
          <div key={index} className="relative space-y-3 rounded-2xl border border-gray-100 bg-white p-4">
            <button
              type="button"
              onClick={() => removeEntry(index)}
              className="absolute right-3 top-3 text-xs font-semibold text-red-600 hover:text-red-700"
            >
              Remove
            </button>
            <div className="grid grid-cols-2 gap-3 pr-16">
              <SelectField
                label="Qualification Type"
                value={entry.qualificationType}
                onChange={(e) => updateEntry(index, { qualificationType: e.target.value })}
                options={QUAL_TYPE_OPTS}
              />
              <Field label="Expiry Date" required={isProfessionalLicense} hint={isProfessionalLicense ? 'Required for Professional License' : undefined}>
                <TextInput type="date" value={entry.expiryDate} onChange={(e) => updateEntry(index, { expiryDate: e.target.value })} />
              </Field>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addEntry}
        className="w-full rounded-xl border border-dashed border-green-300 bg-white py-2.5 text-xs font-semibold text-green-700 hover:bg-green-100"
      >
        + Add Qualification
      </button>
    </div>
  );
}

function ActorsForm({
  f,
  set,
  onClose,
  onSubmit,
  stateOptions,
  lgaOptions,
  wardOptions,
  loadingLgas,
  loadingWards,
  busy,
  error,
  loadingRefs,
  speciesOptions,
  marketOptions,
  deploymentOptions,
  populationOptions,
  institutionOptions,
  simplified = false,
  modulePanel,
  mode = 'create',
  row = null,
  onEdit,
  canEdit = true,
  canSetVerification = true,
}) {
  const [step, setStep] = useState(0);
  const isSecondStep = step === 1;
  const isReviewStep = step === 2;
  const lockedLocation = simplified;
  const isFarmerProducer = String(f.actorType ?? '').trim() === 'Farmer Producer';
  const isValidNigerianPhone = (value) => /^(0[789]\d{9}|\+234[789]\d{8})$/.test(String(value ?? '').trim());
  const basicValid =
    f.fullName.trim().length >= 3 &&
    Boolean(f.actorType) &&
    Boolean(f.sex) &&
    Boolean(f.ageGroup) &&
    isValidNigerianPhone(f.contactPhone) &&
    Boolean(f.state) &&
    Boolean(f.lga);
  const farmerValid = !isFarmerProducer || (
    Boolean(f.productionSystem) && Boolean(f.species_uuid) && Boolean(f.herdSizeClass) && Number(f.farmSize) >= 0.1
  );
  const systemValid = Boolean(f.status) && (!canSetVerification || Boolean(f.verificationStatus));
  const canGoNext = step === 0 ? basicValid : step === 1 ? farmerValid && systemValid : true;

  if (mode === 'view') {
    const statusKey = String(f.status ?? '').trim().toLowerCase();
    const dotCls = VIEW_STATUS_DOT[statusKey] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[statusKey] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {statusKey || 'draft'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <SectionHead title="Actor Registration" />
          <ReviewRow label="Full Name" value={f.fullName} />
          <ReviewRow label="Actor Type" value={f.actorType} />
          <ReviewRow label="Phone Number" value={f.contactPhone} />
          <ReviewRow label="Sex" value={f.sex} />
          <ReviewRow label="Age Group" value={f.ageGroup} />
          <ReviewRow label="Species Specialisation" value={resolveLabels(speciesOptions, f.speciesSpecialisation)} />
          <ReviewRow label="Registration Number" value={row?.registrationNumber || row?.registration_number || 'Assigned automatically on save'} />

          <SectionHead title="Geospatial" />
          <ReviewRow label="State" value={f.state} />
          <ReviewRow label="LGA" value={f.lga} />

          <SectionHead title="System" />
          <ReviewRow label="Status" value={f.status} />
          {canSetVerification ? (
            <ReviewRow label="Verification Status" value={f.verificationStatus} />
          ) : (
            <ReviewRow label="Verification Status" value={simplified ? 'R05 simplified flow' : 'Managed by higher-level roles'} />
          )}
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  const submitLabel = busy
    ? (mode === 'create' ? 'Creating...' : 'Saving...')
    : (mode === 'create' ? 'Create Record' : 'Save Changes');
  // Pill-shaped Previous/Save footer, matching Market Trade & Intelligence's
  // and Livestock Population's Review-step footer exactly (Previous steps
  // back one step, not to the first step).
  const reviewFooter = (
    <>
      <button
        type="button"
        onClick={() => setStep((current) => Math.max(0, current - 1))}
        className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
      >
        Previous
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={busy}
        className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitLabel}
      </button>
    </>
  );

  return (
    <RecordWizard
      steps={[
        { key: 'details', label: 'Actor Details' },
        { key: 'other', label: 'Other Information' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Fill in the details to create a new livestock actor record' : 'Update the details for this record'}
      statusLabel={mode === 'create' ? 'draft' : (String(f.status ?? 'draft').toLowerCase() || 'draft')}
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep((current) => Math.min(current + 1, 2))}
      canGoNext={canGoNext}
      nextLabel={isSecondStep ? 'Review' : 'Next'}
      footer={isReviewStep ? reviewFooter : undefined}
      error={error}
    >
      {!isReviewStep && modulePanel}
      {isReviewStep ? (
        <>
          <SectionHead title="Actor Registration" />
          <ReviewRow label="Full Name" value={f.fullName} />
          <ReviewRow label="Actor Type" value={f.actorType} />
          <ReviewRow label="Phone Number" value={f.contactPhone} />
          <ReviewRow label="Sex" value={f.sex} />
          <ReviewRow label="Age Group" value={f.ageGroup} />
          <ReviewRow label="Species Specialisation" value={resolveLabels(speciesOptions, f.speciesSpecialisation)} />
          <ReviewRow label="Registration Number" value={row?.registrationNumber || row?.registration_number || 'Assigned automatically on save'} />

          <SectionHead title="Geospatial" />
          <ReviewRow label="State" value={f.state} />
          <ReviewRow label="LGA" value={f.lga} />

          {isFarmerProducer && (
            <>
              <SectionHead title="Farmer Registration" />
              <p className="text-xs text-gray-400">This record will also create the linked farmer producer profile.</p>
              <ReviewRow label="Production System" value={f.productionSystem} />
              <ReviewRow label="Primary Species" value={resolveLabel(speciesOptions, f.species_uuid)} />
              <ReviewRow label="Herd Size Class" value={f.herdSizeClass} />
              <ReviewRow label="Farm Size" value={f.farmSize} />
              <ReviewRow label="Herd Registration Number" value={f.herdRegistrationNumber} />
              <ReviewRow label="Population Record" value={resolveLabel(populationOptions, f.population_record_uuid)} />
              <ReviewRow label="Primary Market" value={resolveLabel(marketOptions, f.primary_market_uuid)} />
              <ReviewRow label="Extension Officer Deployment" value={resolveLabel(deploymentOptions, f.extension_officer_deployment_uuid)} />
            </>
          )}

          {(f.qualifications ?? []).length > 0 && (
            <>
              <SectionHead title="Qualification (repeatable)" />
              {(f.qualifications ?? []).map((entry, index) => (
                <ReviewRow
                  key={index}
                  label={entry.qualificationType || `Qualification ${index + 1}`}
                  value={entry.expiryDate ? `Expires ${entry.expiryDate}` : 'No expiry'}
                />
              ))}
            </>
          )}

          <SectionHead title="System" />
          <ReviewRow label="Status" value={f.status} />
          {canSetVerification ? (
            <ReviewRow label="Verification Status" value={f.verificationStatus} />
          ) : (
            <ReviewRow label="Verification Status" value={simplified ? 'R05 simplified flow' : 'Managed by higher-level roles'} />
          )}
        </>
      ) : isSecondStep ? (
        <div className="space-y-5">
          <Notice error={error} loading={loadingRefs} />
          {isFarmerProducer && (
            <div className="space-y-3 rounded-2xl border border-green-100 bg-green-50/60 p-4">
              <SectionHeader
                title="Farmer Registration"
                description={mode === 'create' ? 'Fill this section to create the linked farmer producer record.' : 'Update the linked farmer producer record.'}
              />
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Production System" required value={f.productionSystem} onChange={set('productionSystem')} options={PRODUCTION_SYSTEMS} />
                <SelectField label="Primary Species" required value={f.species_uuid} onChange={set('species_uuid')} options={speciesOptions} placeholder="Select species" disabled={loadingRefs} />
                <SelectField label="Herd Size Class" required value={f.herdSizeClass} onChange={set('herdSizeClass')} options={HERD_CLASS_OPTS} />
                <Field label="Farm Size (hectares)" required hint="Minimum 0.1 ha">
                  <TextInput type="number" value={f.farmSize} onChange={set('farmSize')} placeholder="e.g. 15" />
                </Field>
                <Field label="Herd Registration Number">
                  <TextInput value={f.herdRegistrationNumber} onChange={set('herdRegistrationNumber')} placeholder="Enter herd reg. number" />
                </Field>
                <SelectField label="Population Record" value={f.population_record_uuid} onChange={set('population_record_uuid')} options={populationOptions} placeholder="Select population record" disabled={loadingRefs} />
                <SelectField label="Primary Market" value={f.primary_market_uuid} onChange={set('primary_market_uuid')} options={marketOptions} placeholder="Select market" disabled={loadingRefs} />
                <SelectField label="Extension Officer Deployment" value={f.extension_officer_deployment_uuid} onChange={set('extension_officer_deployment_uuid')} options={deploymentOptions} placeholder="Select deployment" disabled={loadingRefs} />
              </div>
              <div className="rounded-2xl border border-dashed border-green-200 bg-white px-4 py-3 text-xs text-green-700">
                {mode === 'create'
                  ? 'Saving this record will also create the linked farmer producer profile.'
                  : 'Saving will create the linked farmer producer profile if one does not already exist, or update it otherwise.'}
              </div>
            </div>
          )}

          <QualificationsRepeatable
            entries={f.qualifications ?? []}
            onChange={(next) => set('qualifications')({ target: { value: next } })}
          />
          {mode !== 'create' && (
            <p className="-mt-2 text-xs text-gray-400">
              Only new qualifications added here are saved — this actor's existing qualifications aren't listed above.
            </p>
          )}

          <div className="space-y-3">
            <SectionHeader title="System" />
            <div className="grid grid-cols-2 gap-3">
              <SelectField label="Status" required value={f.status} onChange={set('status')} options={STATUS_OPTS} />
              {canSetVerification ? (
                <SelectField label="Verification Status" value={f.verificationStatus} onChange={set('verificationStatus')} options={VERIFY_OPTS} />
              ) : (
                <ReadonlyBox>
                  {simplified ? 'R05 uses the simplified form and does not set verification status here.' : 'Verification status is managed by higher-level roles.'}
                </ReadonlyBox>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <Notice error={error} loading={loadingRefs} />
          <div className="space-y-3">
            <SectionHeader title="Actor Registration" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full Name" required>
                <TextInput value={f.fullName} onChange={set('fullName')} placeholder="Enter full name" />
              </Field>
              <SelectField label="Actor Type" required value={f.actorType} onChange={set('actorType')} options={ACTOR_TYPE_OPTS} />
              <Field label="Phone Number" required hint="Nigerian format: 070/080/090... (11 digits)">
                <PhoneInput value={f.contactPhone} onChange={set('contactPhone')} />
              </Field>
              <SelectField label="Sex" required value={f.sex} onChange={set('sex')} options={SEX_OPTS} />
              <SelectField label="Age Group" required value={f.ageGroup} onChange={set('ageGroup')} options={AGE_GROUP_OPTS} />
            </div>
            <Field label="Species Specialisation" hint="Optional — select all species this actor works with">
              <MultiSelectPicklist
                values={f.speciesSpecialisation ?? []}
                onChange={(values) => set('speciesSpecialisation')({ target: { value: values } })}
                options={speciesOptions}
                placeholder="Select one or more species"
              />
            </Field>
            {mode === 'edit' && (
              <Field label="Registration Number">
                <ReadonlyBox>{row?.registrationNumber || row?.registration_number || 'Not yet assigned'}</ReadonlyBox>
              </Field>
            )}
            {mode === 'create' && (
              <Field label="Registration Number">
                <ReadonlyBox>Assigned automatically on save</ReadonlyBox>
              </Field>
            )}
          </div>

          <div className="space-y-3">
            <SectionHeader title="Geospatial" />
            <div className="grid grid-cols-2 gap-3">
              {!lockedLocation ? (
                <>
                  <SelectField
                    label="State"
                    required
                    value={f.state}
                    onChange={set('state')}
                    options={stateOptions}
                    placeholder="Select state"
                  />
                  <SelectField
                    label="LGA"
                    required
                    value={f.lga}
                    onChange={set('lga')}
                    options={lgaOptions}
                    placeholder={f.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select state first'}
                    disabled={!f.state || loadingLgas}
                  />
                  <SelectField
                    label="Ward"
                    required
                    value={f.wardCommunity}
                    onChange={set('wardCommunity')}
                    options={wardOptions}
                    placeholder={f.lga ? (loadingWards ? 'Loading wards...' : 'Select Ward') : 'Select LGA first'}
                    disabled={!f.lga || loadingWards}
                  />
                </>
              ) : (
                <>
                  <Field label="State" required>
                    <ReadonlyBox>{f.state || 'Not set'}</ReadonlyBox>
                  </Field>
                  <Field label="LGA">
                    <ReadonlyBox>{f.lga || 'Not set'}</ReadonlyBox>
                  </Field>
                  <Field label="Ward">
                    <ReadonlyBox>{f.wardCommunity || 'Not set'}</ReadonlyBox>
                  </Field>
                </>
              )}
            </div>
          </div>
          <SectionDivider />
        </div>
      )}
    </RecordWizard>
  );
}

function DeployForm({
  f,
  set,
  onClose,
  onSubmit,
  actorOptions,
  institutionOptions,
  stateOptions,
  lgaOptions,
  wardOptions,
  loadingLgas,
  loadingWards,
  loadingRefs,
  error,
  busy,
  modulePanel,
  mode = 'create',
  row = null,
  onEdit,
  canEdit = true,
}) {
  const [step, setStep] = useState(0);
  const isReviewStep = step === 1;
  const deployEndDateValid = !f.endDate || !f.startDate || f.endDate >= f.startDate;
  const canGoNext =
    Boolean(f.livestock_actor_uuid) &&
    Boolean(f.actorType) &&
    Boolean(f.institution_uuid) &&
    Boolean(f.deploymentType) &&
    Boolean(f.status) &&
    Boolean(f.state) &&
    Boolean(f.lga) &&
    Boolean(f.startDate) &&
    deployEndDateValid;

  if (mode === 'view') {
    const statusKey = String(f.status ?? '').trim().toLowerCase();
    const dotCls = VIEW_STATUS_DOT[statusKey] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[statusKey] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {statusKey || 'draft'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <SectionHead title="Deployment Details" />
          <ReviewRow label="Livestock Actor" value={resolveLabel(actorOptions, f.livestock_actor_uuid)} />
          <ReviewRow label="Role Category" value={f.actorType} />
          <ReviewRow label="Posting Institution" value={resolveLabel(institutionOptions, f.institution_uuid)} />
          <ReviewRow label="Deployment Status" value={f.status} />
          <ReviewRow label="Deployment Type" value={f.deploymentType} />
          <ReviewRow label="Vehicle Status" value={f.mobilityStatus} />
          <ReviewRow label="State" value={f.state} />
          <ReviewRow label="LGA" value={f.lga} />
          <ReviewRow label="Start Date" value={f.startDate} />
          <ReviewRow label="End Date" value={f.endDate} />
          <ReviewRow label="Supervisor" value={f.supervisor} />
          <ReviewRow label="Responsibilities" value={f.responsibilities} />
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  const reviewFooter = (
    <>
      <button
        type="button"
        onClick={() => setStep(0)}
        className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
      >
        Previous
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={busy}
        className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {busy ? (mode === 'create' ? 'Creating...' : 'Saving...') : (mode === 'create' ? 'Create Record' : 'Save Changes')}
      </button>
    </>
  );

  return (
    <RecordWizard
      steps={[
        { key: 'details', label: 'Deployment Details' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Track the posting, location, and status of a workforce deployment' : 'Update the details for this record'}
      statusLabel="draft"
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep(1)}
      canGoNext={canGoNext}
      nextLabel="Review"
      footer={isReviewStep ? reviewFooter : undefined}
      error={error}
    >
      {!isReviewStep && modulePanel}
      {isReviewStep ? (
        <>
          <SectionHead title="Deployment Details" />
          <ReviewRow label="Livestock Actor" value={resolveLabel(actorOptions, f.livestock_actor_uuid)} />
          <ReviewRow label="Role Category" value={f.actorType} />
          <ReviewRow label="Posting Institution" value={resolveLabel(institutionOptions, f.institution_uuid)} />
          <ReviewRow label="Deployment Status" value={f.status} />
          <ReviewRow label="Deployment Type" value={f.deploymentType} />
          <ReviewRow label="Vehicle Status" value={f.mobilityStatus} />
          <ReviewRow label="State" value={f.state} />
          <ReviewRow label="LGA" value={f.lga} />
          <ReviewRow label="Start Date" value={f.startDate} />
          <ReviewRow label="End Date" value={f.endDate} />
          <ReviewRow label="Supervisor" value={f.supervisor} />
          <ReviewRow label="Responsibilities" value={f.responsibilities} />
        </>
      ) : (
        <div className="space-y-4">
          <Notice error={error} loading={loadingRefs} />
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Livestock Actor" required value={f.livestock_actor_uuid} onChange={set('livestock_actor_uuid')} options={actorOptions} placeholder="Select actor" disabled={loadingRefs} />
            <SelectField label="Role Category" required value={f.actorType} onChange={set('actorType')} options={ACTOR_TYPE_OPTS} />
            <SelectField label="Posting Institution" required value={f.institution_uuid} onChange={set('institution_uuid')} options={institutionOptions} placeholder="Select institution" disabled={loadingRefs} />
            <SelectField label="Deployment Status" required value={f.status} onChange={set('status')} options={['Active', 'Inactive', 'On Leave', 'Completed']} />
            <SelectField label="Deployment Type" required value={f.deploymentType} onChange={set('deploymentType')} options={DEPLOY_TYPE_OPTS} />
            <SelectField label="Vehicle Status" value={f.mobilityStatus} onChange={set('mobilityStatus')} options={MOBILITY_OPTS} />
            <SelectField label="State" required value={f.state} onChange={set('state')} options={stateOptions} placeholder="Select state" />
            <SelectField
              label="LGA"
              required
              value={f.lga}
              onChange={set('lga')}
              options={lgaOptions}
              placeholder={f.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select state first'}
              disabled={!f.state || loadingLgas}
            />
            <SelectField
              label="Ward"
              value={f.wardCommunity}
              onChange={set('wardCommunity')}
              options={wardOptions}
              placeholder={f.lga ? (loadingWards ? 'Loading wards...' : 'Select Ward') : 'Select LGA first'}
              disabled={!f.lga || loadingWards}
            />
            <Field label="Start Date" required>
              <TextInput value={f.startDate} onChange={set('startDate')} type="date" />
            </Field>
            <Field label="End Date" hint={!deployEndDateValid ? 'Must be on or after Start Date' : undefined}>
              <TextInput value={f.endDate} onChange={set('endDate')} type="date" min={f.startDate || undefined} />
            </Field>
            <Field label="Supervisor">
              <TextInput value={f.supervisor} onChange={set('supervisor')} placeholder="Enter supervisor name" />
            </Field>
          </div>
          <SectionDivider />
          <Field label="Responsibilities">
            <TextArea value={f.responsibilities} onChange={set('responsibilities')} placeholder="Optional responsibilities" rows={3} />
          </Field>
        </div>
      )}
    </RecordWizard>
  );
}

function CoopForm({
  f,
  set,
  onClose,
  onSubmit,
  actorOptions,
  speciesOptions,
  stateOptions,
  lgaOptions,
  wardOptions,
  loadingLgas,
  loadingWards,
  loadingRefs,
  error,
  busy,
  modulePanel,
  mode = 'create',
  row = null,
  onEdit,
  canEdit = true,
}) {
  const [step, setStep] = useState(0);
  const isReviewStep = step === 1;
  const canGoNext =
    f.orgName.trim().length >= 3 &&
    Boolean(f.cooperativeType) &&
    f.regNumber.trim().length >= 5 &&
    Boolean(f.state) &&
    Boolean(f.lga) &&
    Boolean(f.primaryCommodity) &&
    Number(f.totalMembers) >= 1 &&
    Number(f.activeMembers) <= Number(f.totalMembers);

  if (mode === 'view') {
    const statusKey = String(f.status ?? '').trim().toLowerCase();
    const dotCls = VIEW_STATUS_DOT[statusKey] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[statusKey] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {statusKey || 'draft'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <SectionHead title="Organisation Details" />
          <ReviewRow label="Organisation Name" value={f.orgName} />
          <ReviewRow label="Organisation Type" value={f.cooperativeType} />
          <ReviewRow label="Reg Number" value={f.regNumber} />
          <ReviewRow label="State" value={f.state} />
          <ReviewRow label="LGA" value={f.lga} />
          <ReviewRow label="Ward / Community" value={f.wardCommunity} />
          <ReviewRow label="Total Members" value={f.totalMembers} />
          <ReviewRow label="Active Members" value={f.activeMembers} />
          <ReviewRow label="Registering Authority" value={f.registeringAuthority} />
          <ReviewRow label="Date of Registration" value={f.dateOfRegistration} />
          <ReviewRow label="Bank Account Status" value={f.bankAccountStatus} />
          <ReviewRow label="Primary Commodity" value={resolveLabel(speciesOptions, f.primaryCommodity)} />
          <ReviewRow label="Primary Activities" value={f.primaryActivities} />
          <ReviewRow label="Contact Phone" value={f.contactPhone} />
          <ReviewRow label="Contact Email" value={f.contactEmail} />
          <ReviewRow label="Status" value={f.status} />
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  const reviewFooter = (
    <>
      <button
        type="button"
        onClick={() => setStep(0)}
        className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
      >
        Previous
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={busy}
        className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {busy ? (mode === 'create' ? 'Creating...' : 'Saving...') : (mode === 'create' ? 'Create Record' : 'Save Changes')}
      </button>
    </>
  );

  return (
    <RecordWizard
      steps={[
        { key: 'details', label: 'Organisation Details' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Capture the cooperative registration details' : 'Update the details for this record'}
      statusLabel="draft"
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep(1)}
      canGoNext={canGoNext}
      nextLabel="Review"
      footer={isReviewStep ? reviewFooter : undefined}
      error={error}
    >
      {!isReviewStep && modulePanel}
      {isReviewStep ? (
        <>
          <SectionHead title="Organisation Details" />
          <ReviewRow label="Organisation Name" value={f.orgName} />
          <ReviewRow label="Organisation Type" value={f.cooperativeType} />
          <ReviewRow label="Reg Number" value={f.regNumber} />
          <ReviewRow label="State" value={f.state} />
          <ReviewRow label="LGA" value={f.lga} />
          <ReviewRow label="Ward / Community" value={f.wardCommunity} />
          <ReviewRow label="Total Members" value={f.totalMembers} />
          <ReviewRow label="Active Members" value={f.activeMembers} />
          <ReviewRow label="Registering Authority" value={f.registeringAuthority} />
          <ReviewRow label="Date of Registration" value={f.dateOfRegistration} />
          <ReviewRow label="Bank Account Status" value={f.bankAccountStatus} />
          <ReviewRow label="Primary Commodity" value={resolveLabel(speciesOptions, f.primaryCommodity)} />
          <ReviewRow label="Primary Activities" value={f.primaryActivities} />
          <ReviewRow label="Contact Phone" value={f.contactPhone} />
          <ReviewRow label="Contact Email" value={f.contactEmail} />
          <ReviewRow label="Status" value={f.status} />
        </>
      ) : (
        <div className="space-y-4">
          <Notice error={error} loading={loadingRefs} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Organisation Name" required>
              <TextInput value={f.orgName} onChange={set('orgName')} placeholder="Enter organisation name" />
            </Field>
            <SelectField label="Organisation Type" required value={f.cooperativeType} onChange={set('cooperativeType')} options={COOP_TYPE_OPTS} />
            <Field label="Reg Number" required hint="Min 5 characters — CAC or other registration number">
              <TextInput value={f.regNumber} onChange={set('regNumber')} placeholder="Enter registration number" />
            </Field>
            <SelectField label="State" required value={f.state} onChange={set('state')} options={stateOptions} placeholder="Select state" />
            <SelectField
              label="LGA"
              required
              value={f.lga}
              onChange={set('lga')}
              options={lgaOptions}
              placeholder={f.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select state first'}
              disabled={!f.state || loadingLgas}
            />
            <SelectField
              label="Ward / Community"
              value={f.wardCommunity}
              onChange={set('wardCommunity')}
              options={wardOptions}
              placeholder={f.lga ? (loadingWards ? 'Loading wards...' : 'Select Ward / Community') : 'Select LGA first'}
              disabled={!f.lga || loadingWards}
            />
            <Field label="Total Members" required hint="≥ 1">
              <TextInput value={f.totalMembers} onChange={set('totalMembers')} type="number" min="1" placeholder="e.g. 150" />
            </Field>
            <Field label="Active Members" required hint="≤ Total Members">
              <TextInput value={f.activeMembers} onChange={set('activeMembers')} type="number" min="0" placeholder="e.g. 120" />
            </Field>
            <SelectField label="Primary Commodity" required value={f.primaryCommodity} onChange={set('primaryCommodity')} options={speciesOptions} placeholder="Select commodity" disabled={loadingRefs} />
            <SelectField label="Registering Authority" value={f.registeringAuthority} onChange={set('registeringAuthority')} options={REG_AUTH_OPTS} />
            <Field label="Date of Registration">
              <TextInput value={f.dateOfRegistration} onChange={set('dateOfRegistration')} type="date" />
            </Field>
            <SelectField label="Bank Account Status" value={f.bankAccountStatus} onChange={set('bankAccountStatus')} options={BANK_STATUS_OPTS} />
          </div>
          <SectionDivider />
          <Field label="Primary Activities">
            <TextArea value={f.primaryActivities} onChange={set('primaryActivities')} placeholder="e.g. Cattle rearing, Milk production" rows={3} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact Phone">
              <PhoneInput value={f.contactPhone} onChange={set('contactPhone')} />
            </Field>
            <Field label="Contact Email">
              <TextInput value={f.contactEmail} onChange={set('contactEmail')} placeholder="name@example.com" />
            </Field>
          </div>
          <SelectField label="Status" value={f.status} onChange={set('status')} options={STATUS_OPTS} />
        </div>
      )}
    </RecordWizard>
  );
}

const FORM_MAP = {
  actors: ActorsForm,
  deployment: DeployForm,
  coop: CoopForm,
};

export default function WorkforceCreateModal({
  isOpen,
  inline = false,
  onClose,
  onCreate,
  defaultSubModule = '',
  mode = 'create',
  row = null,
  onEdit,
  canEdit = true,
}) {
  const { user } = useAuth();
  const isR05 = user?.roleId === 'R05';
  const canSetVerification = user?.roleId !== 'R02' && user?.roleId !== 'R05';
  const [subModule, setSubModule] = useState(defaultSubModule || '');
  const [form, setForm] = useState({ ...(EMPTY_MAP[defaultSubModule] ?? {}) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [linkedFarmerUuid, setLinkedFarmerUuid] = useState(null);
  const shouldLoadLocationRefs = ['actors', 'deployment', 'coop'].includes(subModule);
  const {
    stateOptions,
    lgaOptions,
    loadingStates,
    loadingLgas,
    wardOptions,
    loadingWards,
  } = useLocationOptions(shouldLoadLocationRefs ? form.state : '', shouldLoadLocationRefs ? form.lga : '');

  const {
    actorOptions,
    institutionOptions,
    speciesOptions,
    marketOptions,
    deploymentOptions,
    populationOptions,
    loading: loadingRefs,
    error: refsError,
  } = useWorkforceReferenceOptions();

  const visibleSubModules = useMemo(
    () => (isR05 ? SUB_MODULES.filter((item) => item.value === 'actors') : SUB_MODULES),
    [isR05]
  );

  useEffect(() => {
    if (!isOpen) return;
    const nextSubModule = visibleSubModules.some((item) => item.value === defaultSubModule)
      ? defaultSubModule
      : (visibleSubModules[0]?.value ?? '');
    setSubModule(nextSubModule);

    if (mode !== 'create' && row) {
      const mapper = ROW_MAPPERS[nextSubModule];
      setForm(mapper ? mapper(row) : { ...(EMPTY_MAP[nextSubModule] ?? {}) });
    } else if (mode === 'create') {
      setForm({
        ...(EMPTY_MAP[nextSubModule] ?? {}),
        ...(isR05 && nextSubModule === 'actors'
          ? {
              state: user?.assignedState ?? '',
              lga: user?.assignedLga ?? '',
            }
          : {}),
      });
    }
    setError('');
    setBusy(false);
  }, [defaultSubModule, isOpen, isR05, mode, row, user?.assignedLga, user?.assignedState, visibleSubModules]);

  // ── Actors + Farmer Producer: if the actor being edited is already linked
  // to a farmer producer record, find it so the Farmer Registration section
  // shows its real data (not a blank form) and handleSave knows to update it
  // instead of creating a duplicate.
  useEffect(() => {
    if (mode === 'create' || subModule !== 'actors' || !row) {
      setLinkedFarmerUuid(null);
      return undefined;
    }
    if (String(row.actorType ?? '').trim() !== 'Farmer Producer') {
      setLinkedFarmerUuid(null);
      return undefined;
    }
    const actorUuid = row.uuid ?? row.id;
    let active = true;
    farmerProducersApi.listAll({ per_page: 200 })
      .then((result) => {
        if (!active) return;
        const items = Array.isArray(result?.items) ? result.items : [];
        const linked = items.find((item) => (item.livestock_actor_uuid ?? item.livestockActorUuid) === actorUuid);
        if (!linked) {
          setLinkedFarmerUuid(null);
          return;
        }
        setLinkedFarmerUuid(linked.uuid ?? linked.id ?? null);
        setForm((prev) => ({
          ...prev,
          species_uuid: linked.speciesUuid ?? linked.species_uuid ?? prev.species_uuid,
          productionSystem: linked.productionSystem ?? prev.productionSystem,
          herdSizeClass: linked.herdSizeClass ?? prev.herdSizeClass,
          farmSize: String(linked.farmSize ?? prev.farmSize ?? ''),
          herdRegistrationNumber: linked.farmHerdRegNo ?? linked.herdRegistrationNumber ?? prev.herdRegistrationNumber,
          population_record_uuid: linked.populationRecordUuid ?? linked.population_record_uuid ?? prev.population_record_uuid,
          primary_market_uuid: linked.primaryMarketUuid ?? linked.primary_market_uuid ?? prev.primary_market_uuid,
          extension_officer_deployment_uuid: linked.extensionOfficerDeploymentUuid ?? linked.extension_officer_deployment_uuid ?? prev.extension_officer_deployment_uuid,
        }));
      })
      .catch(() => { if (active) setLinkedFarmerUuid(null); });
    return () => { active = false; };
  }, [mode, subModule, row]);

  const handleClose = () => {
    const nextSubModule = visibleSubModules.some((item) => item.value === defaultSubModule)
      ? defaultSubModule
      : (visibleSubModules[0]?.value ?? '');
    setSubModule(nextSubModule);
    setForm({
      ...(EMPTY_MAP[nextSubModule] ?? {}),
      ...(isR05 && nextSubModule === 'actors'
        ? {
            state: user?.assignedState ?? '',
            lga: user?.assignedLga ?? '',
          }
        : {}),
    });
    setError('');
    setBusy(false);
    onClose();
  };

  const handleSubModuleChange = (value) => {
    if (isR05 && value !== 'actors') return;
    setSubModule(value);
    setForm({
      ...(EMPTY_MAP[value] ?? {}),
      ...(isR05 && value === 'actors'
        ? {
            state: user?.assignedState ?? '',
            lga: user?.assignedLga ?? '',
          }
        : {}),
    });
    setError('');
  };

  const set = (key) => (event) =>
    setForm((previous) => ({
      ...previous,
      [key]: event.target.value,
      ...(key === 'state' ? { lga: '', wardCommunity: '' } : {}),
      ...(key === 'lga' ? { wardCommunity: '' } : {}),
    }));

  const handleCreate = async () => {
    if (!subModule || busy) return;
    setBusy(true);
    setError('');

    try {
      let created;
      if (subModule === 'actors') {
        const actorType = String(form.actorType ?? '').trim();
        const isFarmerProducer = actorType === 'Farmer Producer';

        if (isFarmerProducer) {
          if (!form.productionSystem || !form.species_uuid || !form.herdSizeClass) {
            throw new Error('Production System, Primary Species, and Herd Size Class are required for Farmer Producer actors.');
          }
        }

        created = await livestockActorsApi.create({
          ...form,
          speciesSpecialisation: (form.speciesSpecialisation ?? []).join(', '),
        });

        if (isFarmerProducer) {
          const actorUuid = created?.uuid ?? created?.id ?? '';
          if (!actorUuid) {
            throw new Error('Created actor is missing a uuid, so the linked farmer record could not be created.');
          }

          try {
            await farmerProducersApi.create({
              livestock_actor_uuid: actorUuid,
              species_uuid: form.species_uuid,
              productionSystem: form.productionSystem,
              herdSizeClass: form.herdSizeClass,
              farmSize: form.farmSize,
              herdRegistrationNumber: form.herdRegistrationNumber,
              population_record_uuid: form.population_record_uuid,
              primary_market_uuid: form.primary_market_uuid,
              extension_officer_deployment_uuid: form.extension_officer_deployment_uuid,
            });
          } catch (farmerErr) {
            if (actorUuid) {
              await livestockActorsApi.remove(actorUuid).catch(() => {});
            }
            throw farmerErr;
          }
        }

        // Qualifications are optional/repeatable — a failure here doesn't
        // roll back the actor (unlike the farmer record above, which the
        // actor type requires). Any that fail can still be added afterward
        // from the Actors Qualification tab.
        const actorUuidForQuals = created?.uuid ?? created?.id ?? '';
        const qualifications = (form.qualifications ?? []).filter((q) => q.qualificationType);
        if (actorUuidForQuals && qualifications.length > 0) {
          const failedLabels = [];
          for (const qual of qualifications) {
            try {
              await actorQualificationsApi.create({ ...qual, livestock_actor_uuid: actorUuidForQuals });
            } catch {
              failedLabels.push(qual.qualificationType || 'Qualification');
            }
          }
          if (failedLabels.length > 0) {
            onCreate?.(created);
            setError(`Actor created, but ${failedLabels.length} qualification(s) could not be saved (${failedLabels.join(', ')}).`);
            setBusy(false);
            return;
          }
        }
      } else if (subModule === 'deployment') {
        created = await workforceDeploymentsApi.create(form);
      } else if (subModule === 'coop') {
        created = await cooperativesApi.create(form);
      } else {
        throw new Error('Select a sub module before creating a record.');
      }

      onCreate?.(created);
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create record.');
      setBusy(false);
    }
  };

  // Update path. The exact payload shape per sub module below is copied
  // verbatim from the update logic that used to live in
  // WorkforceInstitutionalAddRecord.jsx's own handleSave (before view/edit
  // was unified into this wizard) — do not add fields beyond what's listed
  // here, since these are the already-correct, tested shapes.
  const handleSave = async () => {
    if (!subModule || busy || !row) return;
    const uuid = row?.uuid ?? row?.id;
    if (!uuid) {
      setError('Missing record identifier; unable to save changes.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      switch (subModule) {
        case 'deployment':
          await workforceDeploymentsApi.update(uuid, {
            actorType: form.actorType,
            status: form.status,
            startDate: form.startDate,
            endDate: form.endDate,
            supervisor: form.supervisor,
            deploymentType: form.deploymentType,
            mobilityStatus: form.mobilityStatus,
            responsibilities: form.responsibilities,
            livestock_actor_uuid: form.livestock_actor_uuid,
            institution_uuid: form.institution_uuid,
            state: form.state,
            lga: form.lga,
            wardCommunity: form.wardCommunity,
          });
          break;
        case 'coop':
          await cooperativesApi.update(uuid, {
            orgName: form.orgName,
            cooperativeType: form.cooperativeType,
            regNumber: form.regNumber,
            state: form.state,
            lga: form.lga,
            totalMembers: form.totalMembers,
            activeMembers: form.activeMembers,
            wardCommunity: form.wardCommunity,
            registeringAuthority: form.registeringAuthority,
            dateOfRegistration: form.dateOfRegistration,
            bankAccountStatus: form.bankAccountStatus,
            primaryCommodity: form.primaryCommodity,
            primaryActivities: form.primaryActivities,
            contactPhone: form.contactPhone,
            contactEmail: form.contactEmail,
            status: form.status,
          });
          break;
        case 'actors':
        default:
          await livestockActorsApi.update(uuid, {
            fullName: form.fullName,
            contactPhone: form.contactPhone,
            actorType: form.actorType,
            sex: form.sex,
            ageGroup: form.ageGroup,
            state: form.state,
            lga: form.lga,
            wardCommunity: form.wardCommunity,
            status: form.status,
            verificationStatus: form.verificationStatus,
            speciesSpecialisation: (form.speciesSpecialisation ?? []).join(', '),
          });

          if (String(form.actorType ?? '').trim() === 'Farmer Producer') {
            if (!form.productionSystem || !form.species_uuid || !form.herdSizeClass) {
              throw new Error('Production System, Primary Species, and Herd Size Class are required for Farmer Producer actors.');
            }
            const farmerPayload = {
              livestock_actor_uuid: uuid,
              species_uuid: form.species_uuid,
              productionSystem: form.productionSystem,
              herdSizeClass: form.herdSizeClass,
              farmSize: form.farmSize,
              herdRegistrationNumber: form.herdRegistrationNumber,
              population_record_uuid: form.population_record_uuid,
              primary_market_uuid: form.primary_market_uuid,
              extension_officer_deployment_uuid: form.extension_officer_deployment_uuid,
            };
            if (linkedFarmerUuid) {
              await farmerProducersApi.update(linkedFarmerUuid, farmerPayload);
            } else {
              await farmerProducersApi.create(farmerPayload);
            }
          }

          // Only newly-added rows reach here — this actor's already-existing
          // qualifications aren't reloaded/resubmitted, matching the note
          // shown under the repeatable section in edit mode.
          {
            const newQualifications = (form.qualifications ?? []).filter((q) => q.qualificationType);
            if (newQualifications.length > 0) {
              const failedLabels = [];
              for (const qual of newQualifications) {
                try {
                  await actorQualificationsApi.create({ ...qual, livestock_actor_uuid: uuid });
                } catch {
                  failedLabels.push(qual.qualificationType || 'Qualification');
                }
              }
              if (failedLabels.length > 0) {
                onCreate?.();
                setError(`Actor saved, but ${failedLabels.length} qualification(s) could not be saved (${failedLabels.join(', ')}).`);
                setBusy(false);
                return;
              }
            }
          }
          break;
      }

      onCreate?.();
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update record.');
      setBusy(false);
    }
  };

  const FormComponent = FORM_MAP[subModule] ?? null;
  const refsLoading = loadingRefs || (shouldLoadLocationRefs ? loadingStates : false);
  const refsBannerError = refsError ? refsError : error;
  const modulePanel = (
    <SubModulePanel
      moduleName="Workforce & Institutional Actors"
      moduleDescription="Select a sub module to open the matching workforce or cooperative record form."
      subModuleOptions={visibleSubModules}
      subModule={subModule}
      onSubModuleChange={handleSubModuleChange}
      disabled={visibleSubModules.length === 1}
    />
  );

  return (
    <CoreCreateModal
      isOpen={isOpen}
      inline={inline}
      onClose={handleClose}
    >
      {FormComponent ? (
        <FormComponent
          key={subModule}
          f={form}
          set={set}
          onClose={handleClose}
          onSubmit={mode === 'create' ? handleCreate : handleSave}
          stateOptions={stateOptions}
          lgaOptions={lgaOptions}
          wardOptions={wardOptions}
          loadingLgas={loadingLgas}
          loadingWards={loadingWards}
          loadingRefs={refsLoading}
          actorOptions={actorOptions}
          institutionOptions={institutionOptions}
          speciesOptions={speciesOptions}
          marketOptions={marketOptions}
          deploymentOptions={deploymentOptions}
          populationOptions={populationOptions}
          busy={busy}
          error={refsBannerError}
          simplified={isR05 && subModule === 'actors'}
          modulePanel={modulePanel}
          mode={mode}
          row={row}
          onEdit={onEdit}
          canEdit={canEdit}
          canSetVerification={canSetVerification}
        />
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
          Choose a sub module to continue.
        </div>
      )}
    </CoreCreateModal>
  );
}
